import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

/**
 * Live preview (Task 6, spec §5/§8.3): markdown-it + DOMPurify with the SAME
 * interpolation the backend applies before markdown parsing (DocumentRenderService
 * / template-variables.ts), except values here are hardcoded sample data — this
 * is an APPROXIMATION of the real render; "Podgląd PDF" fetches the exact,
 * server-rendered result. `[[pagebreak]]` renders as a dashed divider instead
 * of a real page break (there is no pagination concept in an HTML preview).
 */

const md = new MarkdownIt({ html: false });

// Same 25 keys/values as SAMPLE_CONTEXT in
// apps/backend/src/modules/documents/template-variables.ts, pre-flattened and
// pre-resolved (composeAddress/formatYmdToDmy/EMPLOYMENT_TYPE_LABELS already
// applied) — this file only renders an approximate client-side preview, so it
// duplicates literal values rather than the resolver logic.
const SAMPLE_VALUES: Record<string, string> = {
  'firma.nazwa': 'FIRMA DEMO SP. Z O.O.',
  'firma.nip': '1234563218',
  'firma.bdo': '000012345',
  'firma.krs': '0000123456',
  'firma.email': 'kontakt@firmademo.pl',
  'firma.www': 'https://firmademo.pl',
  'firma.ulica': 'ul. Przykładowa 1',
  'firma.kod': '00-001',
  'firma.miasto': 'Warszawa',

  'pracownik.imie': 'Jan',
  'pracownik.nazwisko': 'Kowalski',
  'pracownik.ulica': 'ul. Kwiatowa 5',
  'pracownik.kod': '02-034',
  'pracownik.miasto': 'Warszawa',
  'pracownik.adres': 'ul. Kwiatowa 5, 02-034 Warszawa',
  'pracownik.pesel': '90010112345',
  'pracownik.stanowisko': 'Specjalista ds. sprzedaży',
  'pracownik.typ_umowy': 'umowa o pracę',
  'pracownik.data_zatrudnienia': '01.03.2024',

  'kontrahent.nazwa': 'KONTRAHENT DEMO',
  'kontrahent.nip': '9876543210',
  'kontrahent.adres': 'ul. Handlowa 10, 30-001 Kraków',

  'dokument.data': '01.01.2026',
  'dokument.wersja': 'v1',
  'dokument.numer': 'DOK/2026/0001',

  'wystawca.imie_nazwisko': 'Anna Nowak',
  'wystawca.email': 'anna.nowak@firmademo.pl',
};

// Whitespace-tolerant `{{ firma.nip }}` — mirrors template-variables.ts.
const VARIABLE_PATTERN = /\{\{\s*([a-z_]+\.[a-z_]+)\s*\}\}/g;
// `{{plec:męska/żeńska}}` — mirrors resolveGenderForms() in template-variables.ts;
// the sample employee (Jan Kowalski) is male, so the preview picks the male form.
const PLEC_PATTERN = /\{\{\s*plec:([^/{}\n]+)\/([^/{}\n]+?)\s*\}\}/g;
const PAGEBREAK_LINE = /^\[\[pagebreak\]\]$/;

function substituteVariables(contentMd: string): string {
  return contentMd
    .replace(PLEC_PATTERN, (_full, male: string) => male.trim())
    .replace(VARIABLE_PATTERN, (full, name: string) => {
      const value = SAMPLE_VALUES[name];
      return value !== undefined ? value : full; // unknown — left verbatim
    });
}

/** Renders sanitized preview HTML for a template draft (approximate — not paginated). */
export function renderPreviewHtml(contentMd: string): string {
  const segments = contentMd.split('\n').reduce<string[]>(
    (acc, line) => {
      if (PAGEBREAK_LINE.test(line.trim())) {
        acc.push('');
        return acc;
      }
      acc[acc.length - 1] += `${line}\n`;
      return acc;
    },
    [''],
  );

  const html = segments
    .map((segment) => md.render(substituteVariables(segment)))
    .join('<hr class="pagebreak-marker" />');

  const sanitized = DOMPurify.sanitize(html);

  // Wrap remaining (truly unknown) `{{x.y}}` placeholders so they stand out —
  // safe to do post-sanitize since the match itself is the only content
  // inserted, and it's restricted to the same safe character class as the
  // variable pattern above.
  return sanitized.replace(VARIABLE_PATTERN, (full) => `<mark>${full}</mark>`);
}
